/**
 * ChatContext — состояние внутреннего чата сотрудников.
 *
 * Держит список диалогов и общий счётчик непрочитанных (значок уведомлений
 * в верхней панели), опрашивает сервер раз в 10 секунд. Сама панель чата
 * (ChatDrawer) подписана на этот контекст.
 */
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import GetData from "../../services/getData";

/** Интервал опроса новых сообщений (мс). */
export const CHAT_POLL_INTERVAL = 10000;

const ChatContext = createContext({
    open: false,
    setOpen: () => {},
    unread: 0,
    threads: [],
    userId: 0,
    refreshThreads: async () => null,
    patchThread: () => {},
    openThread: () => {},
    activeId: null,
});

export function useChat() {
    return useContext(ChatContext);
}

const isOk = (data) => Boolean(data) && !(data instanceof Error);

export function ChatProvider({ children }) {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const [threads, setThreads] = useState([]);
    const [userId, setUserId] = useState(0);
    const [activeId, setActiveId] = useState(null);
    const timerRef = useRef(null);

    const refreshThreads = useCallback(async () => {
        const data = await GetData({
            module: "chat",
            block: "threads",
            action: "list",
        });
        if (!isOk(data) || !data.result) {
            return null;
        }
        setThreads(Array.isArray(data.result.threads) ? data.result.threads : []);
        setUnread(Number(data.result.unread) || 0);
        setUserId(Number(data.result.user_id) || 0);
        return data.result;
    }, []);

    useEffect(() => {
        refreshThreads();
        timerRef.current = setInterval(refreshThreads, CHAT_POLL_INTERVAL);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [refreshThreads]);

    const patchThread = useCallback((threadId, patch) => {
        setThreads((prev) =>
            prev.map((item) =>
                Number(item.id) === Number(threadId) ? { ...item, ...patch } : item,
            ),
        );
    }, []);

    const openThread = useCallback((threadId) => {
        setActiveId(threadId == null ? null : Number(threadId));
        setOpen(true);
    }, []);

    const value = useMemo(
        () => ({
            open,
            setOpen,
            unread,
            setUnread,
            threads,
            setThreads,
            userId,
            refreshThreads,
            patchThread,
            activeId,
            setActiveId,
            openThread,
        }),
        [
            open,
            unread,
            threads,
            userId,
            refreshThreads,
            patchThread,
            activeId,
            openThread,
        ],
    );

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export default ChatProvider;
